import { colors } from '@globals/themes/defaultTheme'
import { ListTenantMembersParams, MediaVariantEnum, TenantMember } from '@kudos/http-client'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import Tribute, { TributeItem } from 'tributejs'

// Froala's keycode for Enter. Inlined so this hook doesn't statically import
// `froala-editor` (which would pull the editor into the initial bundle); the
// value is a stable DOM keycode.
export const ENTER_KEYCODE = 13

const MAX_MENTIONS_PER_PAGE = 5
export const AVATAR_SIZE = 24 // Size for avatar in mentions menu
export const DEBOUNCE_DELAY = 300 // Debounce delay in milliseconds for user name search

export type MentionItem = {
  key: string
  value: string
  avatarUrl?: string | null
}

// Minimal types for Tribute internals used in the positioning patch
type TributeRange = { positionMenuAtCaret?: (scrollTo: boolean) => void }
export type TributeWithRange = Tribute<MentionItem> & { range?: TributeRange }

/**
 * Returns the HTML string for an @-mention chip. The `contenteditable="false"`
 * attribute makes the span atomic inside Froala's contenteditable element: the
 * browser cannot place the caret inside it, so any text typed near the chip
 * lands outside it in the normal text flow.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/contenteditable
 */
export const getMentionSpanHtml = (item: MentionItem): string =>
  `<span class="fr-deletable fr-tribute" contenteditable="false" data-id="${item.value}" data-type="mention">${item.key}</span>`

/**
 * Builds the HTML inserted when a mention is chosen from the menu.
 *
 * Tribute calls the collection's `selectTemplate` with `undefined` when the
 * user hits Enter/Tab (or clicks) while the "No results found" menu is
 * showing: its select handlers only guard on `filteredItems` being truthy, and
 * an empty array is truthy, so it still calls `selectItemAtIndex(0)` with
 * `filteredItems[0] === undefined`. Returning `null` makes Tribute insert
 * nothing (`selectItemAtIndex` skips `replaceText` when the template returns
 * null) instead of crashing on `item.original` (Rollbar / SYN-7679).
 */
export const buildMentionSelectHtml = (item?: TributeItem<MentionItem>): string | null =>
  item ? getMentionSpanHtml(item.original) : null

/**
 * Builds the menu contents shown when a search returns no results. The message
 * must be wrapped in an `<li>`: Tribute's menu click handler walks up from the
 * click target until it finds a list item, throwing "cannot find the <li>
 * container for the click" if it reaches the menu root first. Returning bare
 * text caused that crash when the user clicked the "No results found" row
 * (Rollbar / SYN-7679). The `<li>` has no `data-index`, so `selectItemAtIndex`
 * bails out via its `isNaN` guard rather than selecting anything.
 *
 * The `no-match` class is Tribute's own convention for a non-selectable row and
 * carries `cursor: default` (from `tributejs/dist/tribute.css`), so the row
 * doesn't show the pointer cursor used for real, clickable mentions.
 */
export const buildMentionNoMatchHtml = (label: string): string =>
  `<li class="no-match">${label}</li>`

export const normalizeMentionSpans = (el: HTMLElement): void => {
  el.querySelectorAll<HTMLElement>('span[data-type="mention"]').forEach(mentionSpan => {
    mentionSpan.contentEditable = 'false'
  })
}

/**
 * Replaces Tribute's positionMenuAtCaret with a version that uses
 * `position: fixed` + viewport coordinates from a temporary marker element.
 *
 * Tribute's built-in positioning uses `position: absolute` and adds
 * `pageYOffset` to convert viewport coords to page-absolute ones. This breaks
 * when any ancestor of `<body>` introduces a new containing block (e.g. via
 * CSS `transform`, `will-change`, or `filter`). `position: fixed` is immune
 * to this because it always positions relative to the viewport — exactly what
 * `getBoundingClientRect()` returns.
 *
 * On mobile, two additional issues are addressed:
 *  1. Collapsed-range rects from `getBoundingClientRect()` frequently return
 *     all-zeros on mobile Safari/Android. A forced reflow (`offsetHeight`)
 *     after inserting the marker span ensures the browser computes layout
 *     before we read the rect. If the rect is still zero, we fall back to the
 *     editor element's own bounding rect so the menu appears near the editor
 *     rather than at (0, 0).
 *  2. When the virtual keyboard is open, the visual viewport can be offset
 *     from the layout viewport. `position: fixed` uses the layout viewport,
 *     while `getBoundingClientRect()` returns visual-viewport coordinates.
 *     We bridge the gap with `window.visualViewport.offsetTop/offsetLeft` and
 *     use visual-viewport dimensions for overflow checks.
 */
export const patchTributePositioning = (tribute: Tribute<MentionItem>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tributeRange = (tribute as any).range

  tributeRange.positionMenuAtCaret = function (
    this: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    scrollTo: boolean,
  ) {
    const info = this.getTriggerInfo(
      false,
      this.tribute.hasTrailingSpace,
      true,
      this.tribute.allowSpaces,
      this.tribute.autocompleteMode,
    )

    if (typeof info === 'undefined') {
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    if (!this.tribute.positionMenu) {
      this.tribute.menu.style.cssText = 'display: block;'
      return
    }

    // For inputs / textareas fall back to the original textarea helper
    if (!this.isContentEditable(this.tribute.current.element)) {
      const coords = this.getTextAreaOrInputUnderlinePosition(
        this.tribute.current.element,
        info.mentionPosition,
      )
      this.tribute.menu.style.cssText = `top: ${coords.top}px; left: ${coords.left}px; position: absolute; display: block;`
      return
    }

    // --- contenteditable path: marker-based fixed positioning ---
    const sel = this.getWindowSelection()
    if (!sel?.anchorNode) {
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    const doc = this.getDocument() as Document
    const range = doc.createRange()
    try {
      range.setStart(sel.anchorNode, info.mentionPosition)
      range.collapse(true)
    } catch {
      // range.setStart can throw if anchorNode is detached or mentionPosition is
      // out of bounds — hide the menu and bail out without crashing.
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    // Try collapsed-range rect first; fall back to a temp marker if it's zero.
    // Mobile browsers frequently return all-zero rects for collapsed ranges.
    let rect = range.getBoundingClientRect()
    let marker: HTMLSpanElement | null = null

    if (!rect.height && !rect.top && !rect.left) {
      const m = doc.createElement('span')
      m.textContent = '\u200b'
      range.insertNode(m)
      void m.offsetHeight // force reflow so mobile browsers compute layout
      rect = m.getBoundingClientRect()
      marker = m
    }

    // Final fallback: if the rect is still zero (can happen on mobile when the
    // editor is inside a scrollable drawer and the browser hasn't flushed layout),
    // position relative to the editor element itself.
    if (!rect.height && !rect.top && !rect.left) {
      const editorElement = this.tribute.current.element as HTMLElement | null
      if (editorElement) {
        const editorRect = editorElement.getBoundingClientRect()
        rect = new DOMRect(editorRect.left, editorRect.top, 0, 0)
      }
    }

    // Account for visual-viewport offset. On mobile, the virtual keyboard can
    // shift the visual viewport within the layout viewport. position:fixed uses
    // layout-viewport coords, but getBoundingClientRect() returns visual-viewport
    // coords, so we bridge the gap.
    const vv = window.visualViewport
    const vvOffsetTop = vv?.offsetTop ?? 0
    const vvOffsetLeft = vv?.offsetLeft ?? 0

    const top = rect.bottom + vvOffsetTop
    const left = rect.left + vvOffsetLeft

    // Render the menu offscreen first so we can measure its intrinsic
    // dimensions without the browser constraining its layout to the
    // remaining space near a viewport edge.
    this.tribute.menu.style.cssText =
      'top: -9999px; left: -9999px; position: fixed; display: block;'

    const menuW = this.tribute.menu.offsetWidth
    const menuH = this.tribute.menu.offsetHeight

    if (marker) {
      marker.remove()
      sel.anchorNode?.parentNode?.normalize?.()
    }

    const viewportW = vv?.width ?? window.innerWidth
    const viewportH = vv?.height ?? window.innerHeight
    const edgePad = 8

    let finalLeft = left
    let finalTop = top

    if (left + menuW + edgePad > vvOffsetLeft + viewportW) {
      finalLeft = Math.max(vvOffsetLeft + edgePad, vvOffsetLeft + viewportW - menuW - edgePad)
    }

    if (top + menuH + edgePad > vvOffsetTop + viewportH) {
      finalTop = Math.max(vvOffsetTop, rect.top + vvOffsetTop - menuH)
    }

    this.tribute.menu.style.cssText = `top: ${finalTop}px; left: ${finalLeft}px; position: fixed; display: block;`

    if (scrollTo) this.scrollIntoView()
  }
}

/** List params for the mentions lookup: excludes the current member, first page, avatars embedded. */
export const buildMentionSearchParams = (
  memberId: string,
  searchText: string,
): ListTenantMembersParams => {
  const params: ListTenantMembersParams = {
    notIdIn: [memberId],
    suspended: false,
    page: 1,
    pageSize: MAX_MENTIONS_PER_PAGE,
    embeds: ['avatar'],
  }

  if (searchText) {
    params.search = searchText
  }

  return params
}

/** Prefers the AVATAR_SM variant, then the base avatar URL, else null. */
export const mapTenantMembersToMentionItems = (
  results: TenantMember[] | undefined,
): MentionItem[] =>
  (results || []).map((member: TenantMember) => {
    // Extract avatar URL - prefer AVATAR_SM variant, fallback to base URL
    let avatarUrl: string | null = null
    if (member.avatarUrls) {
      const avatarVariant = member.avatarUrls.variants?.find(
        v => v.variant === MediaVariantEnum.AVATAR_SM,
      )
      avatarUrl = avatarVariant?.url ?? member.avatarUrls.url ?? null
    }

    return {
      key: fullNameFromParts(member.firstName, member.lastName),
      value: member.id,
      avatarUrl,
    }
  })

/** Avatar + name row for the Tribute menu; falls back to an initial when there is no avatar. */
export const buildMentionMenuItemHtml = (item: MentionItem): string => {
  const avatarHtml = item.avatarUrl
    ? `<img src="${item.avatarUrl}" alt="" class="tribute-menu-item-avatar" />`
    : `<div class="tribute-menu-item-avatar" style="background-color: ${colors.blueUltraLight}; display: flex; align-items: center; justify-content: center; color: ${colors.blueLight}; font-size: ${Math.round(AVATAR_SIZE / 2)}px; font-weight: 600;">${(item.key.charAt(0) || '').toUpperCase()}</div>`
  return `<div class="tribute-menu-item">${avatarHtml}<span>${item.key}</span></div>`
}
